import React, { useState } from 'react'
import { Message } from './types'
import TranscriptWithAudio from './transcript-with-audio'

interface CallSummaryTopic {
  id: string
  header: string
  text: string
}

interface CallSummary {
  id: number
  header: string
  text?: string
  main_topics?: CallSummaryTopic[]
}

interface CallSentence {
  id: number
  paragraph_id: number
  recording_id: number
  speaker_id: number
  text: string
  start: number
  end: number
}

interface CallParagraph {
  id: number
  speaker: {
    friendly_name: string
    id: number
    user_contact_id: number | null
    user_id: number
  }
  sentences: CallSentence[]
}

interface CallSummaryModalProps {
  show: boolean
  onClose: () => void
  message: Message
  summary: CallSummary
  paragraphs: CallParagraph[]
  hasTranscription: boolean
  isVoicemail: boolean
  callDuration: number
  audioUrl?: string
}

const CallSummaryModal: React.FC<CallSummaryModalProps> = ({
  show,
  onClose,
  message,
  summary,
  paragraphs,
  hasTranscription,
  isVoicemail,
  callDuration,
  audioUrl,
}) => {
  const [mainTopicsExpanded, setMainTopicsExpanded] = useState(false)

  const handleClose = () => {
    setMainTopicsExpanded(false)
    onClose()
  }

  return (
    <TranscriptWithAudio
      show={show}
      onClose={handleClose}
      message={message}
      title={isVoicemail ? 'Voicemail Playback' : 'Call Playback'}
      paragraphs={paragraphs}
      hasTranscription={hasTranscription}
      isVoicemail={isVoicemail}
      callDuration={callDuration}
      audioUrl={audioUrl}
      audioId={`call-summary-${message.id}`}
    >
      {/* Main Topics Section */}
      {Array.isArray(summary?.main_topics) && summary?.main_topics?.length > 0 && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <h6
              className="fw-bold mb-0 text-white"
              style={{ fontSize: '18px' }}
            >
              Main Topics Discussed
            </h6>
            <button
              type="button"
              className="btn btn-link p-0 d-flex align-items-center no-focus-shadow"
              onClick={() => setMainTopicsExpanded(!mainTopicsExpanded)}
              style={{
                fontSize: '14px',
                textDecoration: 'none',
                color: '#4ea6ff',
                fontWeight: 500,
                boxShadow: 'none',
              }}
            >
              {mainTopicsExpanded ? 'Collapse' : 'Expand'}
              <ion-icon
                name={mainTopicsExpanded ? 'chevron-up' : 'chevron-down'}
                style={{ marginLeft: '4px', fontSize: '16px' }}
              />
            </button>
          </div>
          <hr
            className="my-2"
            style={{ borderTop: '1px solid #ffffff', opacity: 0.3 }}
          />

          {!mainTopicsExpanded && (
            <div
              className="text-white mb-3"
              style={{ fontSize: '16px', lineHeight: '1.5' }}
            >
              {summary?.header}
            </div>
          )}

          {mainTopicsExpanded && (
            <div
              style={{
                maxHeight: '20vh',
                overflowY: 'auto',
                paddingRight: '0.5rem',
                marginRight: '-0.5rem',
              }}
            >
              <ul
                className="ps-4 text-white mb-0"
                style={{ listStyleType: 'disc' }}
              >
                {summary?.main_topics?.map((topic: CallSummaryTopic) => (
                  <li key={topic.id} className="mb-2">
                    <strong className="me-1">{topic.header}:</strong>
                    {topic.text}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <hr
        className="my-2"
        style={{ borderTop: '1px solid #ffffff', opacity: 0.3 }}
      />
    </TranscriptWithAudio>
  )
}

export default CallSummaryModal
